import { tienePermiso } from "../auth/session.js";
import { moduloHabilitado } from "../core/modulos.js";
import { BRAND, C, DOC_INFO, NAV_GRUPOS, ROLES_INFO } from "../core/config.js";
import { state } from "../core/state.js";
import { escapeHTML, normalize } from "../core/utils.js";
import { renderActivosFijos } from "../modules/activosFijos.js";
import { getAlertas, renderAlertas } from "../modules/alertas.js";
import { renderAsistenteIA } from "../modules/asistenteIA.js";
import { renderAuditoria } from "../modules/auditoria.js";
import { renderCartera } from "../modules/cartera.js";
import { renderComisiones } from "../modules/comisiones.js";
import { renderConfiguracion } from "../modules/configuracion.js";
import { renderAsientoManualModal, renderContabilidad } from "../modules/contabilidad.js";
import { renderAnticiposModulo, renderCuentasPorPagar, renderFacturasProveedor } from "../modules/cxp.js";
import { renderControlOP } from "../modules/controlOP.js";
import { renderPilotoPedidos, renderPilotoPorOrdenar, renderPilotoRecepciones, renderPilotoTransicion } from "../modules/piloto.js";
import { renderDashboard } from "../modules/dashboard.js";
import { renderDocModule } from "../modules/documentos.js";
import { renderEmpleados } from "../modules/empleados.js";
import { renderFacturas } from "../modules/facturas.js";
import { renderInventario } from "../modules/inventario.js";
import { renderMaestros } from "../modules/maestros.js";
import { renderNomina } from "../modules/nomina.js";
import { renderNotasCredito } from "../modules/notasCredito.js";
import { renderParametrosFiscales } from "../modules/parametrosFiscales.js";
import { renderInformes } from "../modules/informes.js";
import { renderPuestaEnMarcha } from "../modules/puestaEnMarcha.js";
import { renderReportes } from "../modules/reportes.js";
import { renderRespaldos } from "../modules/respaldos.js";
import { renderUsuarios } from "../modules/usuarios.js";
import { attachListeners } from "./actions.js";
import { sincronizarPendientes } from "./pendientes.js";

let __lastRenderedPage = null;

if (typeof window !== 'undefined') {
  window.__mobObserver = window.__mobObserver || null;
  if (!window.__mobObserver) {
    window.__mobObserver = new MutationObserver(() => {
      if (window.innerWidth <= 768) requestAnimationFrame(applyMobileLayout);
    });
  }
  // Se conecta en attachListeners() cuando el root existe
}

export function toggleMobSidebar() {
  state.mobSidebarOpen = !state.mobSidebarOpen;
  const sb = document.getElementById("mob-sidebar");
  const ov = document.getElementById("mob-overlay");
  if (!sb || !ov) return;
  sb.classList.toggle("mob-open", state.mobSidebarOpen);
  ov.classList.toggle("mob-open", state.mobSidebarOpen);
}

export function applyMobileLayout() {
  if (window.innerWidth > 768) return;

  // ── Función reutilizable para adaptar un contenedor ──────────
  function adaptContainer(root, tableClass) {
    // Envolver tablas en scroll horizontal
    root.querySelectorAll("table").forEach(t => {
      if (t.closest("." + tableClass)) return;
      const wrap = document.createElement("div");
      wrap.className = tableClass;
      wrap.style.cssText = "overflow-x:auto;-webkit-overflow-scrolling:touch;max-width:100%;";
      t.parentNode.insertBefore(wrap, t);
      wrap.appendChild(t);
      // El encabezado de los ítems se fija con `position:sticky;top:44px` para
      // que acompañe al desplazar en escritorio. Dentro de este envoltorio, que
      // es un contenedor de scroll propio, ese desplazamiento se calcula contra
      // la caja y el encabezado termina PINTADO ENCIMA de la primera fila: en
      // el celular se veían los títulos (Cant, Bolsas, Observ.) pero no los
      // campos. En móvil el encabezado no necesita fijarse: se deja quieto.
      t.querySelectorAll("thead th").forEach(th => {
        if (window.getComputedStyle(th).position === "sticky") th.style.setProperty("position", "static", "important");
      });
      // En una pantalla de celular caben dos columnas: sin avisar, las demás
      // (cantidad, bolsas, observaciones) parecen no existir.
      if (wrap.scrollWidth > wrap.clientWidth + 4) {
        const pista = document.createElement("div");
        pista.style.cssText = "font-size:10.5px;color:#64748b;padding:4px 2px 0;";
        pista.textContent = "↔ Desliza la tabla para ver las demás columnas";
        wrap.parentNode.insertBefore(pista, wrap.nextSibling);
      }
    });
    // Grids de columnas fijas (1fr 1fr..., repeat(N,...) con N numérico) → 1 col.
    // repeat(auto-fit/auto-fill,...) queda intacto: ya es responsive por sí mismo.
    root.querySelectorAll("div[style]").forEach(el => {
      const s = el.getAttribute("style") || "";
      if (s.includes("grid-template-columns") &&
          (s.includes("1fr 1fr") || /repeat\(\d/.test(s))) {
        el.style.gridTemplateColumns = "1fr";
      }
      if (s.includes("display:flex") && !s.includes("flex-direction:column") && el.children.length >= 3) {
        el.style.flexWrap = "wrap";
      }
    });
  }

  // 1. Contenido principal
  const mc = document.getElementById("main-content");
  if (mc) adaptContainer(mc, "erp-mob-scroll");

  // 2. Modales abiertos (pueden abrirse tras el render principal)
  document.querySelectorAll('div[style*="position:fixed"][style*="top:0"]').forEach(overlay => {
    const box = overlay.querySelector('[data-action="prevent-close"], [onclick*="stopPropagation"], div[style*="background:white"]');
    if (!box) return;
    // Scroll horizontal en tablas dentro de modal
    adaptContainer(box, "erp-mob-scroll-modal");
    // Asegurar que la caja del modal no desborde
    box.style.setProperty("max-width", "96vw", "important");
    box.style.setProperty("width", "96vw", "important");
    box.style.setProperty("max-height", "88vh", "important");
    box.style.setProperty("overflow-y", "auto", "important");
  });
}

export function navigateTo(page) {
  if (!tienePermiso(page)) {
    alert("No tienes permiso para acceder a este módulo con tu rol actual (" + (ROLES_INFO[state.usuarioRol]?.label || state.usuarioRol || "sin rol") + ").");
    page = "dashboard";
  } else if (!moduloHabilitado(page)) {
    alert("Este módulo no está habilitado para tu empresa.");
    page = "dashboard";
  }
  state.page = page;
  try { localStorage.setItem("erp:page", page); } catch {}
  // Cerrar sidebar en móvil al navegar
  state.mobSidebarOpen = false;
  render();
}

// ─── Foco y cursor: estado del usuario, igual que el scroll ─────────────────
// Hay campos que re-renderizan en cada tecla (los de cantidad y precio al
// cruzar una OCP con la factura del proveedor, el de "Ajustar precios", los
// buscadores). Como render() reconstruye TODO el DOM, el input donde se está
// escribiendo se reemplaza por uno nuevo, el foco se pierde y solo entra la
// PRIMERA letra: "no me deja escribir el número completo".
//
// La posición se identifica por la ruta de índices desde #root: en un
// re-render de la misma pantalla la estructura es la misma y solo cambian los
// datos (el mismo supuesto que usa la restauración de scroll de abajo). Se
// confirma además que el elemento encontrado sea el mismo campo —etiqueta,
// tipo, estilo y nombre— antes de devolverle el foco.
function rutaDesdeRaiz(el) {
  const ruta = [];
  let n = el;
  while (n && n.id !== "root") {
    const padre = n.parentElement;
    if (!padre) return null;
    ruta.unshift(Array.prototype.indexOf.call(padre.children, n));
    n = padre;
  }
  return n ? ruta : null;
}

function firmaCampo(el) {
  return [el.tagName, el.type || "", el.getAttribute("style") || "",
    el.dataset.field || "", el.dataset.form || "", el.dataset.item || ""].join("|");
}

function capturarFoco() {
  const el = document.activeElement;
  if (!el || (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA")) return null;
  const ruta = rutaDesdeRaiz(el);
  if (!ruta) return null;
  let ini = null, fin = null;
  // Los <input type="number"> no exponen selección: se restaura solo el foco.
  try { ini = el.selectionStart; fin = el.selectionEnd; } catch { /* sin selección */ }
  return { ruta, ini, fin, firma: firmaCampo(el) };
}

function restaurarFoco(f) {
  if (!f) return;
  // Si el propio render ya dejó el foco en otra parte, mandar eso.
  if (document.activeElement && document.activeElement !== document.body) return;
  let n = document.getElementById("root");
  for (const i of f.ruta) {
    if (!n || !n.children[i]) return;
    n = n.children[i];
  }
  if (!n || firmaCampo(n) !== f.firma) return;   // no es el mismo campo
  n.focus();
  // focus() puede disparar un onfocus que seleccione todo el texto: el cursor
  // se repone después para que la siguiente tecla no borre lo ya escrito.
  if (f.ini != null) { try { n.setSelectionRange(f.ini, f.fin); } catch { /* no aplica */ } }
}

export function render() {
  const root = document.getElementById("root");
  if (!root) { console.error("❌ No se encontró el elemento #root"); return; }
  const _foco = capturarFoco();
  // render() reconstruye TODO el HTML en cada acción/click, lo que reemplaza el
  // contenedor con scroll y el del modal abierto por nodos nuevos (scroll en 0).
  // Guardamos la posición actual para restaurarla después de reconstruir el DOM,
  // así un clic o una edición no regresan la vista al inicio de la página/modal.
  // Si la página cambió (navegación real, no un re-render de la misma página),
  // sí queremos empezar arriba, como es el comportamiento esperado al navegar.
  const _mismaPagina = state.page === __lastRenderedPage;
  const _prevMainContent = document.getElementById("main-content");
  const _scrollMain = (_mismaPagina && _prevMainContent) ? _prevMainContent.scrollTop : 0;
  // El sidebar es UI persistente: conservamos SIEMPRE su scroll (incluso al
  // navegar a otro módulo), si no, entrar a un módulo del fondo de la lista
  // (p.ej. Administración) haría que la barra "suba" al tope tras reconstruir
  // el DOM. Distinto del área de contenido, que sí vuelve arriba al navegar.
  const _prevSidebar = document.getElementById("mob-sidebar");
  const _scrollSidebar = _prevSidebar ? _prevSidebar.scrollTop : 0;
  const _prevModalBox = document.querySelector('[data-action="prevent-close"]');
  const _scrollModal = _prevModalBox ? _prevModalBox.scrollTop : 0;
  // Contenedores con scroll propio (tablas anchas con overflow:auto/overflow-x:auto,
  // envoltorios de móvil, etc.): guardamos su posición por orden de aparición, ya que
  // en el mismo render de la misma página la estructura es la misma y solo cambian datos.
  const _capturarScrollContenedores = (root) => root
    ? Array.from(root.querySelectorAll('[style*="overflow"]')).map(el => ({ top: el.scrollTop, left: el.scrollLeft }))
    : [];
  const _scrollContenedoresMain = (_mismaPagina && _prevMainContent) ? _capturarScrollContenedores(_prevMainContent) : [];
  const _scrollContenedoresModal = _prevModalBox ? _capturarScrollContenedores(_prevModalBox) : [];
  try {
    const docColor = ["CT","OP","OCP","RC","FACT","NC"].includes(state.page) ? DOC_INFO[state.page].color : null;
    const contentBg = docColor ? `background:${docColor}06;` : "";
    const accentBar = docColor ? `<div style="height:5px;background:${docColor};border-radius:5px 5px 0 0;margin:-24px -28px 20px -28px;"></div>` : "";
    let contenido = "";
    try { contenido = renderContent(); } catch(eContent) {
      console.error("❌ Error en renderContent() página=" + state.page + ":", eContent);
      contenido = `<div style="padding:22px;text-align:center;color:#ef4444;"><div style="font-size:48px;margin-bottom:10px;">⚠️</div><h2>Error al cargar módulo</h2><p style="color:#6b7280;">Módulo: <strong>${state.page}</strong></p><pre style="background:#f8fafc;padding:9px;border-radius:6px;font-size:10.5px;text-align:left;max-width:600px;margin:16px auto;overflow:auto;">${eContent.message}\n${eContent.stack||''}</pre><button onclick="navigateTo('dashboard')" style="margin-top:12px;padding:10px 24px;background:#12264D;color:white;border:none;border-radius:6px;cursor:pointer;font-size:14px;">← Volver al Panel</button></div>`;
    }
    root.innerHTML = `
      <div id="main-layout" style="display:flex;height:100vh;background:${C.bg};">
        <div id="mob-overlay" class="${state.mobSidebarOpen ? "mob-open" : ""}" onclick="toggleMobSidebar()"></div>
        ${renderSidebar()}
        <div id="main-content" style="flex:1;overflow-y:auto;padding:24px 28px;${contentBg}">
          <div id="mob-topbar">
            <button id="mob-ham" onclick="toggleMobSidebar()" title="Menú">☰</button>
            <span style="font-weight:800;color:#12264D;font-size:12px;letter-spacing:.3px;">${BRAND.producto}</span>
            <span style="margin-left:auto;font-size:10px;color:#64748b;font-weight:600;letter-spacing:.5px;">${state.page.toUpperCase()}</span>
          </div>
          ${accentBar}
          ${(() => {
            const _al = getAlertas();
            const _n = _al.total;
            if (_n === 0) return "";
            const _urgent = _al.opsEntrega.some(a => a.dias <= 0)
              || _al.cxpDescuento.some(a => a.dias <= 0);
            const _bg  = _urgent ? "#fdf5f5" : "#fdfaf3";
            const _bor = _urgent ? "#eecccc" : "#ece1c2";
            const _col = _urgent ? "#8f3d3d" : "#7a6320";
            const _dot = _urgent ? "#dc2626" : "#d9a520";
            return `<div onclick="navigateTo('alertas')" style="cursor:pointer;background:${_bg};border:1px solid ${_bor};border-radius:9px;padding:9px 14px;margin-bottom:14px;display:flex;align-items:center;gap:11px;transition:opacity .15s;" onmouseover="this.style.opacity='.82'" onmouseout="this.style.opacity='1'">
              <span style="width:8px;height:8px;border-radius:50%;background:${_dot};flex:0 0 auto;box-shadow:0 0 0 3px ${_dot}22;"></span>
              <div style="flex:1;">
                <span style="font-weight:700;color:${_col};font-size:12px;">${_n} alerta${_n!==1?'s':''} pendiente${_n!==1?'s':''}</span>
                <span style="font-size:10.5px;color:${_col};opacity:.7;margin-left:8px;">${_urgent?'Requiere atención':'Ver detalles'}</span>
              </div>
              <span style="font-size:10.5px;color:${_col};font-weight:700;white-space:nowrap;opacity:.85;">Ver todas →</span>
            </div>`;
          })()}
          ${contenido}
        </div>
      </div>
      ${state._asientoManualDraft ? renderAsientoManualModal(state._asientoManualDraft) : (state.modal || "")}
    `;
    attachListeners();
    // Los avisos de "documentos a medias" se repasan tras cada cambio: si el
    // documento que nombraban ya se completó, se van solos en vez de quedarse
    // señalando algo cerrado hasta la próxima recarga.
    sincronizarPendientes();
    const _restaurarScrollContenedores = (root, guardados) => {
      if (!root || guardados.length === 0) return;
      root.querySelectorAll('[style*="overflow"]').forEach((el, i) => {
        if (guardados[i]) { el.scrollTop = guardados[i].top; el.scrollLeft = guardados[i].left; }
      });
    };
    const _newMainContent = document.getElementById("main-content");
    if (_newMainContent) _newMainContent.scrollTop = _scrollMain;
    _restaurarScrollContenedores(_newMainContent, _scrollContenedoresMain);
    const _newSidebar = document.getElementById("mob-sidebar");
    if (_newSidebar) _newSidebar.scrollTop = _scrollSidebar;
    const _newModalBox = document.querySelector('[data-action="prevent-close"]');
    if (_newModalBox) _newModalBox.scrollTop = _scrollModal;
    _restaurarScrollContenedores(_newModalBox, _scrollContenedoresModal);
    restaurarFoco(_foco);
    __lastRenderedPage = state.page;
  } catch(e) {
    console.error("❌ Error crítico en render():", e);
    root.innerHTML = `<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;flex-direction:column;font-family:sans-serif;background:#f8fafc;"><div style="text-align:center;padding:22px;background:white;border-radius:16px;box-shadow:0 4px 24px rgba(0,0,0,.1);max-width:560px;width:90%;"><div style="font-size:56px;margin-bottom:10px;">⚠️</div><h2 style="color:#12264D;margin:0 0 8px;">Error al iniciar ${BRAND.producto}</h2><p style="color:#6b7280;margin:0 0 9px;font-size:14px;">Ocurrió un error inesperado. Intenta limpiar el caché del navegador (Ctrl+Shift+R) o abre en modo incógnito.</p><details style="text-align:left;background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;padding:9px;font-size:11.5px;"><summary style="cursor:pointer;font-weight:700;color:#dc2626;">Ver detalle del error</summary><pre style="margin:8px 0 0;overflow:auto;">${e.message}\n${e.stack||''}</pre></details><button onclick="localStorage.clear();location.reload()" style="margin-top:16px;padding:10px 24px;background:#dc2626;color:white;border:none;border-radius:6px;cursor:pointer;font-size:14px;">🗑️ Limpiar datos y reiniciar</button></div></div>`;
  }
}

export function renderSidebar() {
  // ── Grupos colapsables ──────────────────────────────────────────
  // Al ingresar al ERP el sidebar se presenta contraído: todos los grupos
  // arrancan cerrados (solo se ven el Panel General y los títulos de grupo).
  // El usuario abre el grupo que necesita; su elección se conserva en la sesión.
  if (!state.sidebarGroups) state.sidebarGroups = {
    comercial: false, compras: false, inventarios: false, facturacion: false,
    contabilidad: false, talento: false, informes: false, administracion: false
  };
  const sg = state.sidebarGroups;

  const GRUPOS = NAV_GRUPOS;

  function navBtn(item) {
    const active = state.page === item.id;
    const col = item.color || C.accent;
    let badge = "";
    if (item.id === "alertas") {
      const n = getAlertas().total;
      badge = n > 0 ? `<span style="margin-left:auto;background:#ef4444;color:white;font-size:10px;font-weight:800;border-radius:6px;padding:1px 7px;">${n}</span>` : "";
    }
    return `<button data-nav="${item.id}" style="
      width:100%;padding:8px 12px 8px 28px;border:none;
      border-left:3px solid ${active ? col : "transparent"};
      border-radius:0 8px 8px 0;
      background:${active ? col + "15" : "transparent"};
      color:${active ? col : C.text};
      font-weight:${active ? 700 : 500};
      font-size:12px;cursor:pointer;text-align:left;
      font-family:inherit;display:flex;align-items:center;gap:6px;
    ">${item.icon} ${item.label}${badge}</button>`;
  }

  // Dashboard siempre visible (fuera de grupos)
  const dashActive = state.page === "dashboard";
  const dashBtn = `<button data-nav="dashboard" style="
    width:100%;padding:6px 9px;border:none;
    border-left:3px solid ${dashActive ? C.accent : "transparent"};
    border-radius:0 8px 8px 0;
    background:${dashActive ? C.accent + "15" : "transparent"};
    color:${dashActive ? C.accent : C.text};
    font-weight:${dashActive ? 700 : 500};
    font-size:12px;cursor:pointer;text-align:left;
    font-family:inherit;display:flex;align-items:center;gap:6px;
  ">📊 Panel General</button>`;

  const gruposHTML = GRUPOS
    .map(g => ({ ...g, items: g.items.filter(it => tienePermiso(it.id) && moduloHabilitado(it.id)) }))
    .filter(g => g.items.length > 0)
    .map(g => {
    const open = sg[g.id] !== false;
    const anyActive = g.items.some(it => state.page === it.id);
    const headerColor = anyActive ? C.accent : C.text;
    return `
      <div style="margin-bottom:2px;">
        <button data-action="toggle-sidebar-group" data-group="${g.id}"
          style="width:100%;padding:6px 10px;border:none;background:${anyActive?C.accent+"10":"transparent"};
            border-radius:6px;cursor:pointer;text-align:left;font-family:inherit;
            display:flex;align-items:center;gap:7px;color:${headerColor};">
          <span style="font-size:11.5px;">${g.icon}</span>
          <span style="font-size:10.5px;font-weight:800;letter-spacing:.6px;flex:1;">${g.label.toUpperCase()}</span>
          <span style="font-size:10.5px;color:${C.muted};transition:transform .2s;
            transform:rotate(${open?"0":"-90"}deg);">▾</span>
        </button>
        ${open ? `<div style="margin-top:2px;">${g.items.map(navBtn).join("")}</div>` : ""}
      </div>
    `;
  }).join("");

  const items = dashBtn + gruposHTML;

  // Búsqueda global
  const globalQuery = state.globalSearch || "";
  let globalResultsHTML = "";
  if (globalQuery.trim().length >= 2) {
    const q = normalize(globalQuery);
    const results = [];
    // Buscar en clientes
    (state.data.clientes||[]).forEach(c => { if (normalize(c.nombre||"").includes(q)||normalize(c.nit||"").includes(q)) results.push({ icon:"👥", label: c.nombre, sub: "Cliente", action:`data-action="ir-maestro" data-tipo="clientes" data-id="${c.id}"` }); });
    // Buscar en proveedores
    (state.data.proveedores||[]).forEach(c => { if (normalize(c.nombre||"").includes(q)||normalize(c.nit||"").includes(q)) results.push({ icon:"🏭", label: c.nombre, sub: "Proveedor", action:`data-action="ir-maestro" data-tipo="proveedores" data-id="${c.id}"` }); });
    // Buscar en productos
    (state.data.productos||[]).forEach(p => { if (normalize(p.nombre||"").includes(q)) results.push({ icon:"📦", label: p.nombre, sub: "Producto", action:`data-action="ir-maestro" data-tipo="productos" data-id="${p.id}"` }); });
    // Buscar en documentos
    const docTipos = [
      { key:"cotizaciones", tipo:"CT", icon:"📋" },
      { key:"pedidos", tipo:"OP", icon:"🔧" },
      { key:"ocp", tipo:"OCP", icon:"🛒" },
      { key:"remisiones", tipo:"RC", icon:"📮" },
      { key:"facturas", tipo:"FACT", icon:"💰" },
      { key:"notas_credito", tipo:"NC", icon:"🧾" }
    ];
    docTipos.forEach(({ key, tipo, icon }) => {
      (state.data[key]||[]).forEach(doc => {
        const cli = (state.data.clientes||[]).find(c=>c.id===doc.cliente_id);
        const prov = (state.data.proveedores||[]).find(p=>p.id===doc.proveedor_id);
        const nombre = cli?.nombre || prov?.nombre || "";
        if (normalize(doc.numero||"").includes(q) || normalize(nombre).includes(q)) {
          results.push({ icon, label: doc.numero, sub: `${tipo} · ${nombre}`, action:`data-action="ir-a-documento" data-tipo="${tipo}" data-id="${doc.id}"` });
        }
      });
    });
    const slice = results.slice(0, 8);
    if (slice.length > 0) {
      globalResultsHTML = `<div style="position:absolute;top:100%;left:0;right:0;background:white;border:1px solid ${C.border};border-radius:0 0 10px 10px;box-shadow:0 8px 24px rgba(0,0,0,.12);z-index:200;max-height:300px;overflow-y:auto;">
        ${slice.map(r => `<div ${r.action} style="padding:6px 10px;cursor:pointer;border-bottom:1px solid ${C.border};display:flex;align-items:center;gap:8px;" onmouseover="this.style.background='#f8fafc'" onmouseout="this.style.background=''">
          <span style="font-size:16px;">${r.icon}</span>
          <div><div style="font-size:11.5px;font-weight:700;color:${C.text};">${escapeHTML(r.label)}</div><div style="font-size:10px;color:${C.muted};">${escapeHTML(r.sub)}</div></div>
        </div>`).join("")}
        ${results.length > 8 ? `<div style="padding:6px 12px;font-size:10.5px;color:${C.muted};text-align:center;">… y ${results.length-8} resultado${results.length-8>1?"s":""} más</div>` : ""}
      </div>`;
    } else {
      globalResultsHTML = `<div style="position:absolute;top:100%;left:0;right:0;background:white;border:1px solid ${C.border};border-radius:0 0 10px 10px;box-shadow:0 8px 24px rgba(0,0,0,.12);z-index:200;padding:6px 10px;font-size:11.5px;color:${C.muted};">Sin resultados para "${escapeHTML(globalQuery)}"</div>`;
    }
  }

  return `
    <div id="mob-sidebar" class="${state.mobSidebarOpen ? "mob-open" : ""}" style="width:240px;background:white;border-right:1px solid ${C.border};padding:9px;overflow-y:auto;display:flex;flex-direction:column;">
      <div style="padding:11px;margin-bottom:9px;background:linear-gradient(160deg,#0B132B,#12264D);border-radius:16px;border:1.5px solid rgba(0,229,255,.22);box-shadow:0 6px 16px rgba(11,19,43,.28);display:flex;align-items:center;justify-content:center;">
        <img src="${window.ISOTIPO}" alt="${BRAND.producto}" style="width:100%;max-width:128px;height:auto;object-fit:contain;display:block;"/>
      </div>
      <div style="position:relative;margin-bottom:10px;">
        <input id="global-search-input" data-search="global" type="text" placeholder="🔍 Buscar clientes, docs..." value="${escapeHTML(globalQuery)}" autocomplete="off"
          style="width:100%;box-sizing:border-box;padding:7px 10px;border:1px solid ${C.border};border-radius:6px;font-size:11.5px;color:${C.text};font-family:inherit;outline:none;"/>
        ${globalResultsHTML}
      </div>
      <nav style="display:flex;flex-direction:column;gap:3px;flex:1;">
        ${items}
      </nav>
      ${(() => {
        const rolInfo = ROLES_INFO[state.usuarioRol] || { label: state.usuarioRol || "Sin rol", icon: "👤" };
        return `<div style="border-top:1px solid ${C.border};padding-top:10px;margin-top:6px;">
          <div style="display:flex;align-items:center;gap:8px;padding:6px 8px;">
            <span style="font-size:16px;">${rolInfo.icon}</span>
            <div style="flex:1;min-width:0;">
              <div style="font-size:11.5px;font-weight:700;color:${C.text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHTML(state.usuarioNombre || state.usuarioEmail || "Usuario")}</div>
              <div style="font-size:10px;color:${C.muted};">${escapeHTML(rolInfo.label)}</div>
            </div>
          </div>
          <button data-action="abrir-seguridad-mfa" style="width:100%;margin-top:4px;padding:7px 8px;border:1px solid ${C.border};background:transparent;border-radius:6px;font-size:11.5px;font-weight:600;color:${C.text};cursor:pointer;font-family:inherit;">🔐 Verificación en dos pasos</button>
          <button data-action="cerrar-sesion" style="width:100%;margin-top:4px;padding:7px 8px;border:1px solid ${C.border};background:transparent;border-radius:6px;font-size:11.5px;font-weight:600;color:#dc2626;cursor:pointer;font-family:inherit;">🚪 Cerrar sesión</button>
        </div>`;
      })()}
      <div style="font-size:10px;color:${C.dim};text-align:center;padding-top:8px;">${BRAND.producto} v5.1</div>
    </div>
  `;
}

export function renderContent() {
  if (state.page === "dashboard") return renderDashboard();
  if (["CT","OP","OCP","RC"].includes(state.page)) return renderDocModule(state.page);
  if (state.page === "FACT") return renderFacturas();
  if (state.page === "NC") return renderNotasCredito();
  if (state.page === "comisiones") return renderComisiones();
  if (state.page === "nomina") return renderNomina();
  if (state.page === "activos-fijos") return renderActivosFijos();
  if (state.page === "maestros") return renderMaestros();
  if (state.page === "empleados") return renderEmpleados();
  if (state.page === "parametros-fiscales") return renderParametrosFiscales();
  if (state.page === "auditoria") return renderAuditoria();
  if (state.page === "reportes") return renderReportes();
  if (state.page === "informes") return renderInformes();
  if (state.page === "puesta-en-marcha") return renderPuestaEnMarcha();
  if (state.page === "control-op") return renderControlOP();
  if (state.page === "piloto-pedidos") return renderPilotoPedidos();
  if (state.page === "piloto-transicion") return renderPilotoTransicion();
  if (state.page === "piloto-por-ordenar") return renderPilotoPorOrdenar();
  if (state.page === "piloto-recepciones") return renderPilotoRecepciones();
  if (state.page === "cxp") return renderCuentasPorPagar();
  if (state.page === "fact-prov") return renderFacturasProveedor();
  if (state.page === "cartera") return renderCartera();
  if (state.page === "anticipos") return renderAnticiposModulo();
  if (state.page === "inventario") return renderInventario();
  if (state.page === "alertas") return renderAlertas();
  if (state.page === "asistente-ia") return renderAsistenteIA();
  if (state.page === "contabilidad") return renderContabilidad();
  if (state.page === "respaldos") return renderRespaldos();
  if (state.page === "usuarios") return renderUsuarios();
  if (state.page === "config") return renderConfiguracion();
  return "<div>Página no encontrada</div>";
}

import { registerActions } from "./actions.js";

registerActions({
  "toggle-sidebar-group": (d, el, e) => {{
      if (!state.sidebarGroups) state.sidebarGroups = {};
      const grp = d.group;
      state.sidebarGroups[grp] = !state.sidebarGroups[grp];
      render();
      return;
    }},
});
